import { type } from "os";
import { elementAt } from "rxjs/operator/elementAt";
import { EventProvider } from "../../providers/event/event";
import { StorageProvider } from "../../providers/storage-service/storage-service";
import { GpsTracker } from "../gps-tracker";
import { LocationProvider } from "../location-handler";
import { StrackerUtils } from "../sencha-tracker/stracker-utils";

export class RunStatistics {
    userId: number = null;
    eventId: number = null;
    eventCid: number = null;
    hasRoute: boolean = false;
    totalDistance: number;
    totalTime: number;
    totalTimeFormatted: string;
    eventName: string;
    kmStatistics: OneKmStatistics[];
    routePoints: any[];

    providers: RunStatisticsProviders;
    event: any;

    constructor(providers: RunStatisticsProviders, event) {
        this.providers = providers;
        this.event = event;
        this.eventId = event.eventId;
        this.eventName = event.eventName;
        this.kmStatistics = [];
    }

    async add() {
        console.log('ADD_KM_STATISTICS: ');

        this.updateMainRunStatistics();

        let km = this.calculateCurrentKm();
        let time = this.calculateTimePerKm();
        let timeFormatted = StrackerUtils.transormSeconds(time);
        let elevation = GpsTracker.lastAltitude;
        let gain = Math.floor(GpsTracker.altitudeGainPerKm);
        let loss = Math.floor(GpsTracker.altitudeLossPerKm);
        let coords = this.getCurrentCoords();

        this.kmStatistics.push({
            km: km,
            time: time,
            timeFormatted: timeFormatted,
            elevation: elevation,
            coords: coords,
            gain: gain,
            loss: loss
        });
    }

    async updateMainRunStatistics() {
        this.writeRoutePoints();
        this.totalDistance = GpsTracker.sumDistance;
        this.totalTime = this.providers.locater.getCurrentDuration();
        this.totalTimeFormatted = StrackerUtils.transormSeconds(this.totalTime);
    }

    writeRoutePoints() {
        this.routePoints = Object.assign([], this.providers.locater.getMapPath());
    }

    calculateCurrentKm() {
        return Math.floor(GpsTracker.sumDistance / 1000);
    }

    calculateTimePerKm() {
        let timekm = 0;

        if (this.kmStatistics.length > 0) {
            let sum = 0;
            for (let i = 0; i < this.kmStatistics.length; i++) {
                sum += this.kmStatistics[i].time;
            }
            timekm = this.totalTime - sum;
        } else {
            timekm = this.totalTime;
        }

        return timekm;
    }

    getCurrentCoords() {
        return this.routePoints[this.routePoints.length - 1];
    }

    writeUserId(userId: number) {
        this.userId = userId;
    }

    writeEventCid(eventCid: number) {
        this.eventCid = eventCid;
    }

    save() {
        if (!this.isEmpty()) {
            let key = 'statistics-results-' + this.eventId + "-" + this.userId;
            let wrappedStatistics = this.getWrappedStatistics();

            this.providers.localDb.saveData(wrappedStatistics, key).catch((error) => {
                throw new Error("Error while saving run statistics data to local storage: " + error)
            });
        } else {
            console.warn('Trying to save statistics-results to local storage but neither userId or eventId is null');
        }
    }

    getWrappedStatistics(): WrappedStatistics {
        return {
            userId: this.userId,
            eventId: this.eventId,
            eventCid: this.eventCid,
            totalTime: this.totalTime,
            totalDistance: this.totalDistance,
            totalTimeFormatted: this.totalTimeFormatted,
            eventName: this.eventName,
            kmStatistics: this.kmStatistics,
            routePoints: this.routePoints,
            hasRoute: this.hasRoute
        };
    }

    reset() {
        this.totalDistance = 0;
        this.totalTime = 0;
        this.kmStatistics = [];
        this.routePoints = [];
    }

    delete() {
        if (!this.isEmpty()) {
            let key = 'statistics-results-' + this.eventId + "-" + this.userId;

            this.providers.localDb.removeData(key).catch((error) => {
                throw new Error('Error: while clearing statistics results: ' + error);
            });
        } else {
            console.warn('Trying to delete statistics-results from local storage but neither userId or eventId is null');
        }
    }

    load() {
        console.log('load: ');
        if (!this.isEmpty()) {
            let key = 'statistics-results-' + this.eventId + "-" + this.userId;
            this.providers.localDb.checkForExistance(key).then((status) => {
                if (status) this.providers.localDb.getSaved(key).then((data: WrappedStatistics) => {
                    console.log('dataLoad: ' + JSON.stringify(data));
                    this.userId = data.userId;
                    this.eventId = data.eventId;
                    this.eventCid = data.eventCid;
                    this.totalTime = data.totalTime;
                    this.totalTimeFormatted = data.totalTimeFormatted;
                    this.totalDistance = data.totalDistance;
                    this.eventName = data.eventName;
                    this.kmStatistics = Object.assign([], data.kmStatistics);
                    this.routePoints = Object.assign([], data.routePoints);
                });
            })
        } else {
            console.warn('Trying to load statistics-results from local storage but neither userId or eventId is null');
        }
    }

    isElevationEmpty(): boolean {
        if (this.kmStatistics.length <= 0 || !this.kmStatistics[0].elevation || typeof this.kmStatistics[0].elevation === "undefined") {
            return true;
        }
        return false;
    }

    isEmpty(): boolean {
        return (!this.eventId || !this.userId) ? true : false;
    }

    enableRouting() {
        this.hasRoute = true;
    }

    disableRouting() {
        this.hasRoute = false;
    }
}

export interface RunStatisticsProviders {
    localDb: StorageProvider;
    locater: LocationProvider;
    event: EventProvider;
}

export interface WrappedStatistics {
    userId: number;
    eventId: number;
    eventCid: any;
    totalTime: number;
    totalDistance: number;
    totalTimeFormatted: string;
    eventName: string;
    kmStatistics: OneKmStatistics[];
    routePoints: any[];
    hasRoute: boolean;
}

export interface OneKmStatistics {
    km: number;
    time: number;
    timeFormatted: string;
    elevation: number;
    coords: any;
    gain;
    loss;
}


// WEBPACK FOOTER //
// /src/tracker/run-statistics/run-statistics.ts